import type { User } from '../types';

export type UserProfileCompletionField = 'phoneNumber' | 'githubUrl';

type UserIdentity = Pick<User, 'id' | 'email'> | null | undefined;
type UserContactSource = Pick<User, 'phoneNumber' | 'linkedinUrl' | 'githubUrl' | 'facebookUrl' | 'mediaLinks'> | null | undefined;

const PROFILE_COMPLETION_FIELDS: Array<{ key: UserProfileCompletionField; label: string }> = [
  { key: 'phoneNumber', label: 'phone number' },
  { key: 'githubUrl', label: 'GitHub' },
];

export interface UserContactLink {
  key: 'phone' | 'linkedin' | 'github' | 'facebook';
  label: string;
  href: string;
}

export function normalizeUserIdentity(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function getUserIdentitySet(user: UserIdentity): Set<string> {
  const identities = new Set<string>();

  const normalizedId = normalizeUserIdentity(user?.id);
  const normalizedEmail = normalizeUserIdentity(user?.email);

  if (normalizedId) {
    identities.add(normalizedId);
  }

  if (normalizedEmail) {
    identities.add(normalizedEmail);
  }

  return identities;
}

export function usersMatch(a: UserIdentity, b: UserIdentity): boolean {
  const aIdentities = getUserIdentitySet(a);
  const bIdentities = getUserIdentitySet(b);

  if (!aIdentities.size || !bIdentities.size) {
    return false;
  }

  return Array.from(aIdentities).some((identity) => bIdentities.has(identity));
}

export function normalizeProfileUrl(value?: string | null): string | null {
  const raw = String(value || '').trim();
  if (!raw) {
    return null;
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  return `https://${raw}`;
}

export function normalizePhoneNumber(value?: string | null): string | null {
  const raw = String(value || '').trim();
  return raw || null;
}

export function getUserContactLinks(user: UserContactSource): UserContactLink[] {
  const links: UserContactLink[] = [];

  const phoneNumber = normalizePhoneNumber(user?.phoneNumber);
  if (phoneNumber) {
    links.push({
      key: 'phone',
      label: phoneNumber,
      href: `tel:${phoneNumber.replace(/[^+\d]/g, '')}`,
    });
  }

  const linkedinUrl = normalizeProfileUrl(user?.linkedinUrl);
  if (linkedinUrl) {
    links.push({ key: 'linkedin', label: 'LinkedIn', href: linkedinUrl });
  }

  const githubUrl = normalizeProfileUrl(user?.githubUrl);
  if (githubUrl) {
    links.push({ key: 'github', label: 'GitHub', href: githubUrl });
  }

  const facebookUrl = normalizeProfileUrl(user?.facebookUrl);
  if (facebookUrl) {
    links.push({ key: 'facebook', label: 'Facebook', href: facebookUrl });
  }

  // Add mediaLinks from the flexible array
  if (user?.mediaLinks && Array.isArray(user.mediaLinks)) {
    for (const mediaLink of user.mediaLinks) {
      const url = normalizeProfileUrl(mediaLink.url);
      if (url) {
        links.push({
          key: 'facebook', // Use 'facebook' as a generic fallback key for custom media
          label: mediaLink.name,
          href: url,
        });
      }
    }
  }

  return links;
}

export function getIncompleteProfileFields(
  user: Pick<User, UserProfileCompletionField> | null | undefined,
): Array<{ key: UserProfileCompletionField; label: string }> {
  return PROFILE_COMPLETION_FIELDS.filter(({ key }) => !String(user?.[key] || '').trim());
}

export function getUserProfileCompletion(user: Pick<User, UserProfileCompletionField> | null | undefined) {
  const missingFields = getIncompleteProfileFields(user);
  const totalFields = PROFILE_COMPLETION_FIELDS.length;
  const completedFields = totalFields - missingFields.length;
  const percent = totalFields === 0 ? 100 : Math.round((completedFields / totalFields) * 100);

  return {
    totalFields,
    completedFields,
    percent,
    missingFields,
  };
}

export function getProfileCompletionWidthClass(percent: number): string {
  if (percent >= 100) {
    return 'w-full';
  }

  if (percent >= 67) {
    return 'w-2/3';
  }

  if (percent >= 34) {
    return 'w-1/3';
  }

  return 'w-[8%]';
}

export function isUserProfileComplete(user: Pick<User, UserProfileCompletionField> | null | undefined): boolean {
  return getIncompleteProfileFields(user).length === 0;
}

export function getUserProfilePath(user: UserIdentity, currentUser?: UserIdentity): string {
  if (usersMatch(user, currentUser)) {
    return '/app/profile';
  }

  const targetId = normalizeUserIdentity(user?.id) || normalizeUserIdentity(user?.email);
  return targetId ? `/app/users/${encodeURIComponent(targetId)}` : '/app/profile';
}